/**
 * Recommended products in search flydown (desktop + mobile).
 * Source of truth is Garden demand top-movers so this surface matches admin.
 * A shop-wide stale-while-revalidate cache keeps search open effectively O(1).
 */
(function () {
  'use strict';

  if (window.__mrMapleDemandPopularNowInitialized) return;
  window.__mrMapleDemandPopularNowInitialized = true;

  const ROW_SELECTOR = '[data-demand-popular-row]';
  const FALLBACK_SCOPE_SELECTOR = '[data-demand-popular-scope]';
  const RECOMMENDATIONS_LIMIT = 3;
  const TOP_PRODUCTS_ENDPOINT = '/apps/garden/v1/demand/top-products?limit=3&availability=all';
  const NEWEST_PRODUCTS_ENDPOINT = '/collections/all/products.json?limit=3&sort_by=created-descending';
  const CACHE_STALE_TTL_MS = 30 * 60 * 1000;
  const CACHE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
  const CACHE_KEY_PREFIX = 'mrmaple:search-top-movers:v4';
  const ANIMATION_PLAYED_DELAY_MS = 420;

  /** Cache keyed by handle → { price, image, url } */
  const productCache = new Map();

  /** In-memory cache for top movers. { cacheKey, products, fetchedAt, staleAt, expiresAt } */
  let recommendedProductsCache = null;
  let hydrationPromise = null;
  let hasRendered = false;

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    const rawValue = typeof url === 'string' ? url : typeof url === 'object' && url && typeof url.url === 'string' ? url.url : '';
    const normalized = rawValue.trim();
    if (!normalized) return '';
    if (normalized.startsWith('//')) return `https:${normalized}`;
    if (normalized.startsWith('/')) return normalized;
    if (/^https?:\/\//i.test(normalized)) return normalized;
    return '';
  }

  function normalizeProductUrl(url) {
    if (!url) return '';
    const normalized = String(url).trim();
    if (!normalized) return '';
    if (normalized.startsWith('/')) return normalized;
    if (/^https?:\/\//i.test(normalized)) return normalized;
    return '';
  }

  function formatMoneyFromCents(cents) {
    const safeCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const moneyFormat = window.SHOP_MONEY_FORMAT || (window.Theme && window.Theme.moneyFormat) || undefined;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(safeCents, moneyFormat);
      } catch (_) {
        // Fall through to Intl formatter fallback.
      }
    }

    const currencyCode =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || document.documentElement.getAttribute('data-currency') || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(safeCents / 100);
    } catch (_) {
      return `$${(safeCents / 100).toFixed(2)}`;
    }
  }

  function formatApiPrice(price) {
    if (!price || typeof price !== 'object') return '';
    const amount = typeof price.amount === 'string' ? price.amount.trim() : price.amount;
    if (amount == null || amount === '') return '';
    const currencyCode = String(price.currencyCode || '').trim() || 'USD';
    return formatAmountPrice(amount, currencyCode);
  }

  function formatAmountPrice(amount, currencyCode) {
    const numericAmount = Number(amount);
    const normalizedCurrency = String(currencyCode || '').trim() || 'USD';
    if (!Number.isFinite(numericAmount)) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: normalizedCurrency,
      }).format(numericAmount);
    } catch (_) {
      return `${normalizedCurrency} ${numericAmount.toFixed(2)}`;
    }
  }

  function formatScalarPrice(amount, currencyCode) {
    if (amount == null || amount === '') return '';

    const rawString = typeof amount === 'string' ? amount.trim() : '';
    const numericAmount = Number(rawString || amount);
    if (!Number.isFinite(numericAmount)) return '';

    const isIntegerLike = Number.isInteger(numericAmount) && !rawString.includes('.');

    // Integer payloads at this magnitude are ambiguous between cents and dollars.
    // Prefer hydrating canonical Shopify product JSON instead of guessing wrong.
    if (isIntegerLike && Math.abs(numericAmount) >= 1000) {
      return '';
    }

    return formatAmountPrice(numericAmount, currencyCode);
  }

  function getCurrencyKey() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || document.documentElement.getAttribute('data-currency') || 'USD';
  }

  function getRecommendationCacheKey() {
    return `${CACHE_KEY_PREFIX}:${getCurrencyKey()}:${RECOMMENDATIONS_LIMIT}`;
  }

  function parsePersistentCache(cacheKey) {
    if (
      recommendedProductsCache &&
      recommendedProductsCache.cacheKey === cacheKey &&
      Array.isArray(recommendedProductsCache.products) &&
      recommendedProductsCache.products.length > 0
    ) {
      return recommendedProductsCache;
    }

    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.cacheKey !== cacheKey || !Array.isArray(parsed.products) || parsed.products.length === 0) {
        window.localStorage.removeItem(cacheKey);
        return null;
      }

      if (Number(parsed.expiresAt || 0) <= Date.now()) {
        window.localStorage.removeItem(cacheKey);
        return null;
      }

      recommendedProductsCache = parsed;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writePersistentCache(cacheKey, products) {
    const now = Date.now();
    const entry = {
      cacheKey,
      products,
      fetchedAt: now,
      staleAt: now + CACHE_STALE_TTL_MS,
      expiresAt: now + CACHE_MAX_AGE_MS,
    };

    recommendedProductsCache = entry;

    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (_) {
      // Best-effort cache only.
    }
  }

  function getCachedRecommendedProducts(options) {
    const config = options && typeof options === 'object' ? options : {};
    const entry = parsePersistentCache(getRecommendationCacheKey());
    if (!entry) {
      return {
        entry: null,
        products: null,
        isFresh: false,
      };
    }

    const now = Date.now();
    const isFresh = Number(entry.staleAt || 0) > now;
    if (!isFresh && !config.allowStale) {
      return {
        entry,
        products: null,
        isFresh: false,
      };
    }

    return {
      entry,
      products: entry.products,
      isFresh,
    };
  }

  function setCachedRecommendedProducts(products) {
    writePersistentCache(getRecommendationCacheKey(), products);
  }

  function hashString(value) {
    const input = String(value || '');
    let hash = 2166136261;

    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return String(hash >>> 0);
  }

  function getProductsRenderSignature(products) {
    const signatureSource = (products || []).map((product) => [
      String((product && product.handle) || '').trim(),
      cleanTitle(product && product.title),
      normalizeProductUrl(product && product._resolvedUrl),
      normalizeImageUrl(product && product._resolvedImage),
      String((product && product._resolvedPrice) || '').trim(),
    ]);

    return `products:${hashString(JSON.stringify(signatureSource))}`;
  }

  function getFallbackRenderSignature(markup) {
    return `fallback:${hashString(markup)}`;
  }

  function markAnimationPlayed(rowElement) {
    if (rowElement.getAttribute('data-demand-popular-animation-played') === 'true') {
      return;
    }

    window.setTimeout(() => {
      rowElement.setAttribute('data-demand-popular-animation-played', 'true');
    }, ANIMATION_PLAYED_DELAY_MS);
  }

  function shouldSkipRender(rowElement, renderSignature) {
    return Boolean(renderSignature) && rowElement.getAttribute('data-demand-popular-render-signature') === renderSignature && rowElement.children.length > 0;
  }

  function rowsAlreadyRendered(rowElements) {
    return (
      rowElements.length > 0 &&
      rowElements.every((rowElement) => {
        const state = rowElement.getAttribute('data-demand-popular-state');
        return (
          Boolean(rowElement.getAttribute('data-demand-popular-render-signature')) &&
          rowElement.children.length > 0 &&
          (state === 'loaded' || state === 'fallback')
        );
      })
    );
  }

  async function loadProductJson(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;

    const loader = window.MrMapleProductJsonLoader;
    if (loader && typeof loader.load === 'function') {
      return loader.load(normalizedHandle);
    }

    const response = await fetch(`/products/${encodeURIComponent(normalizedHandle)}.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  /**
   * Fetch product JSON from Shopify's /products/{handle}.js endpoint.
   * Returns { price, image, url } — all strings (empty string on failure).
   */
  async function fetchProductByHandle(handle) {
    if (!handle) return { price: '', image: '', url: '', title: '' };
    if (productCache.has(handle)) return productCache.get(handle);

    const empty = { price: '', image: '', url: '', title: '' };

    try {
      const payload = await loadProductJson(handle);

      if (!payload || typeof payload !== 'object') {
        productCache.set(handle, empty);
        return empty;
      }

      const price = formatMoneyFromCents(payload && typeof payload.price === 'number' ? payload.price : 0);

      const featuredImage = (payload && payload.featured_image) || (payload && Array.isArray(payload.images) ? payload.images[0] : '') || '';
      const image = normalizeImageUrl(featuredImage);
      const url = normalizeProductUrl(payload && payload.url) || `/products/${encodeURIComponent(handle)}`;

      const title = cleanTitle(payload && payload.title);
      const result = { price, image, url, title };
      productCache.set(handle, result);
      return result;
    } catch (_) {
      productCache.set(handle, empty);
      return empty;
    }
  }

  function cleanTitle(raw) {
    return (raw || 'Untitled product')
      .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/i, '')
      .replace(/\s*\|\s*DOES NOT SHIP\s*$/i, '')
      .replace(/DOES NOT SHIP\s*$/i, '')
      .trim();
  }

  function buildDesktopCard(product) {
    const title = escapeHtml(cleanTitle(product.title));
    const href = escapeHtml(normalizeProductUrl(product._resolvedUrl) || '#');
    const image = normalizeImageUrl(product._resolvedImage || '');
    const imageMarkup = image ? `<img src="${escapeHtml(image)}" alt="${title}" loading="lazy" width="80" height="80">` : '';

    return `
      <a class="search-flydown--popular-card" href="${href}">
        <span class="search-flydown--popular-card-image">${imageMarkup}</span>
        <span class="search-flydown--popular-card-info">
          <span class="search-flydown--popular-card-title">${title}</span>
          <span class="search-flydown--popular-card-price">${escapeHtml(product._resolvedPrice || '')}</span>
        </span>
      </a>
    `;
  }

  function buildMobileCard(product) {
    const title = escapeHtml(cleanTitle(product.title));
    const href = escapeHtml(normalizeProductUrl(product._resolvedUrl) || '#');
    const image = normalizeImageUrl(product._resolvedImage || '');
    const imageMarkup = image ? `<img src="${escapeHtml(image)}" alt="${title}" loading="lazy" width="60" height="60">` : '';

    return `
      <a class="mh-search-ql__popular-card" href="${href}">
        <span class="mh-search-ql__popular-img">${imageMarkup}</span>
        <span class="mh-search-ql__popular-info">
          <span class="mh-search-ql__popular-name">${title}</span>
          <span class="mh-search-ql__popular-price">${escapeHtml(product._resolvedPrice || '')}</span>
        </span>
      </a>
    `;
  }

  function normalizeRecommendedHandle(item) {
    if (!item || typeof item !== 'object') return '';
    if (item.handle) return String(item.handle).trim();
    const rawUrl = String(item.url || item.productUrl || item.href || '').trim();
    if (!rawUrl) return '';
    const match = rawUrl.match(/\/products\/([^/?#]+)/i);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]).trim();
    } catch (_) {
      return String(match[1]).trim();
    }
  }

  function normalizeRecommendedProduct(item) {
    if (!item || typeof item !== 'object') return null;

    const handle = normalizeRecommendedHandle(item);
    if (!handle) return null;

    const normalizedProduct = {
      ...item,
      handle,
      title: cleanTitle(item.title || ''),
      _resolvedImage:
        normalizeImageUrl(item._resolvedImage) ||
        normalizeImageUrl(item.image) ||
        normalizeImageUrl(item.featured_image) ||
        normalizeImageUrl(item.featuredImage && item.featuredImage.url) ||
        '',
      _resolvedUrl:
        normalizeProductUrl(item._resolvedUrl) ||
        normalizeProductUrl(item.url) ||
        normalizeProductUrl(item.productUrl) ||
        normalizeProductUrl(item.href) ||
        `/products/${encodeURIComponent(handle)}`,
      _resolvedPrice:
        String(item._resolvedPrice || '').trim() || formatApiPrice(item.price) || formatScalarPrice(item.price, item.currency || item.currencyCode) || '',
    };

    return normalizedProduct;
  }

  function dedupeProducts(products) {
    const seen = new Set();

    return (products || []).filter((product) => {
      const handle = String((product && product.handle) || '').trim();
      if (!handle || seen.has(handle)) return false;
      seen.add(handle);
      return true;
    });
  }

  /**
   * Cascade: demand top-movers → newest fallback.
   * Returns raw product objects with at least { handle, title }.
   */
  async function fetchRecommendedProducts() {
    // 1. Demand top-products
    try {
      const res = await fetch(TOP_PRODUCTS_ENDPOINT, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Status ' + res.status);
      const data = await res.json();
      if (data && Array.isArray(data.products) && data.products.length > 0) {
        return dedupeProducts(data.products.slice(0, RECOMMENDATIONS_LIMIT).map(normalizeRecommendedProduct).filter(Boolean));
      }
    } catch (_) {
      /* try newest endpoint fallback when the demand endpoint is unavailable */
    }

    // 2. Newest products fallback when demand rows are unavailable or empty.
    try {
      const res = await fetch(NEWEST_PRODUCTS_ENDPOINT, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Fallback failed');
      const data = await res.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      return dedupeProducts(
        products.slice(0, RECOMMENDATIONS_LIMIT).map((p) => {
          const rawPrice = p.variants?.[0]?.price;
          const priceCents = typeof rawPrice === 'string' && rawPrice.includes('.') ? Math.round(parseFloat(rawPrice, 10) * 100) : Number(rawPrice) || 0;
          const imageUrl = normalizeImageUrl(p.featured_image || (p.images && p.images[0]?.src));
          const url = normalizeProductUrl(p.url) || (p.handle ? `/products/${encodeURIComponent(p.handle)}` : '');
          return {
            handle: p.handle,
            title: p.title,
            _resolvedPrice: formatMoneyFromCents(priceCents),
            _resolvedImage: imageUrl,
            _resolvedUrl: url,
          };
        }),
      );
    } catch (_) {
      /* all sources exhausted */
    }

    return [];
  }

  function getRowStateContainer(rowElement) {
    return rowElement.closest(FALLBACK_SCOPE_SELECTOR) || rowElement.parentElement || rowElement;
  }

  function setRowState(rowElement, state) {
    rowElement.setAttribute('data-demand-popular-state', state);
  }

  function readFallbackMarkup(rowElement) {
    const scope = getRowStateContainer(rowElement);
    const variant = rowElement.getAttribute('data-demand-popular-row') || 'desktop';
    const template = scope.querySelector(`[data-demand-popular-fallback="${variant}"]`);
    if (!(template instanceof HTMLTemplateElement)) return '';
    return (template.innerHTML || '').trim();
  }

  function renderProductsToRows(rowElements, products) {
    const renderSignature = getProductsRenderSignature(products);

    rowElements.forEach((rowElement) => {
      if (shouldSkipRender(rowElement, renderSignature)) {
        setRowState(rowElement, 'loaded');
        markAnimationPlayed(rowElement);
        return;
      }

      const variant = rowElement.getAttribute('data-demand-popular-row') || 'desktop';
      const cardHtml = products
        .map((product) => {
          if (variant === 'mobile') {
            return buildMobileCard(product);
          }
          return buildDesktopCard(product);
        })
        .join('');

      if (cardHtml) {
        rowElement.innerHTML = cardHtml;
        rowElement.setAttribute('data-demand-popular-render-signature', renderSignature);
        setRowState(rowElement, 'loaded');
        markAnimationPlayed(rowElement);
      }
    });
  }

  function showFallbackRows(rowElements) {
    rowElements.forEach((rowElement) => {
      const fallbackMarkup = readFallbackMarkup(rowElement);
      if (!fallbackMarkup) {
        setRowState(rowElement, 'error');
        return;
      }

      const renderSignature = getFallbackRenderSignature(fallbackMarkup);
      if (shouldSkipRender(rowElement, renderSignature)) {
        setRowState(rowElement, 'fallback');
        markAnimationPlayed(rowElement);
        return;
      }

      rowElement.innerHTML = fallbackMarkup;
      rowElement.setAttribute('data-demand-popular-render-signature', renderSignature);
      setRowState(rowElement, 'fallback');
      markAnimationPlayed(rowElement);
    });
  }

  async function hydratePopularRows(rowElementsInput) {
    const rowElements = rowElementsInput || Array.from(document.querySelectorAll(ROW_SELECTOR));
    if (rowElements.length === 0) return;

    const cachedState = getCachedRecommendedProducts({ allowStale: true });
    const cachedProducts = Array.isArray(cachedState.products) ? cachedState.products : [];
    if (cachedProducts.length > 0) {
      renderProductsToRows(rowElements, cachedProducts);
      hasRendered = true;
    }

    if (cachedState.isFresh && cachedProducts.length > 0) {
      return;
    }

    const rawProducts = await fetchRecommendedProducts();
    if (rawProducts.length === 0) {
      if (cachedProducts.length === 0) {
        showFallbackRows(rowElements);
      }
      return;
    }

    const enrichedProducts = await Promise.all(
      rawProducts.map(async (product) => {
        const handle = String(product.handle || '').trim();
        const serverImage = normalizeImageUrl(product._resolvedImage) || normalizeImageUrl(product.image);
        const serverUrl =
          normalizeProductUrl(product._resolvedUrl) ||
          normalizeProductUrl(product.url) ||
          normalizeProductUrl(product.productUrl) ||
          normalizeProductUrl(product.href);
        const serverPrice =
          String(product._resolvedPrice || '').trim() ||
          formatApiPrice(product.price) ||
          formatScalarPrice(product.price, product.currency || product.currencyCode);

        const needsFallback = Boolean(handle) && (!serverImage || !serverUrl || !serverPrice);
        const shopifyData = needsFallback ? await fetchProductByHandle(handle) : { price: '', image: '', url: '' };
        return {
          ...product,
          title: product.title || shopifyData.title || '',
          _resolvedImage: serverImage || normalizeImageUrl(shopifyData.image) || '',
          _resolvedUrl: serverUrl || normalizeProductUrl(shopifyData.url) || (handle ? `/products/${encodeURIComponent(handle)}` : ''),
          _resolvedPrice: serverPrice || shopifyData.price || '',
        };
      }),
    );

    const validProducts = enrichedProducts.filter((p) => {
      const url = normalizeProductUrl(p._resolvedUrl);
      return Boolean(url && url !== '#');
    });

    if (validProducts.length === 0) {
      throw new Error('No valid products after enrichment');
    }

    setCachedRecommendedProducts(validProducts);
    renderProductsToRows(rowElements, validProducts);
  }

  function startHydrationOnce() {
    const rowElements = Array.from(document.querySelectorAll(ROW_SELECTOR));
    if (rowElements.length === 0) return Promise.resolve();

    const cachedState = getCachedRecommendedProducts({ allowStale: true });
    const hasCachedProducts = Array.isArray(cachedState.products) && cachedState.products.length > 0;
    const hasRenderedRows = rowsAlreadyRendered(rowElements);

    if (hasCachedProducts) {
      if (!hasRenderedRows) {
        renderProductsToRows(rowElements, cachedState.products);
      }
      hasRendered = true;
      if (cachedState.isFresh) {
        return Promise.resolve();
      }
    } else if (hasRendered && hasRenderedRows) {
      return Promise.resolve();
    } else if (!hasRendered) {
      rowElements.forEach((rowElement) => setRowState(rowElement, 'loading'));
    }

    if (hydrationPromise) return hydrationPromise;
    if (hasRendered && cachedState.isFresh) return Promise.resolve();

    hydrationPromise = hydratePopularRows(rowElements)
      .then(() => {
        hasRendered = true;
      })
      .catch((error) => {
        if (!hasCachedProducts) {
          showFallbackRows(rowElements);
          console.debug('[Demand Popular Now] Falling back to static cards', error);
          throw error;
        }

        console.debug('[Demand Popular Now] Keeping cached top movers after refresh failure', error);
      })
      .finally(() => {
        hydrationPromise = null;
      });

    return hydrationPromise;
  }

  function installTriggers() {
    const searchInputSelector = '[data-live-search-input], [data-mobile-search-input]';

    document.addEventListener(
      'focusin',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.matches(searchInputSelector)) {
          void startHydrationOnce().catch(() => {});
        }
      },
      true,
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (target.closest('[data-live-search]') || target.closest('[data-mobile-home-search]') || target.closest('[data-mobile-search-input]')) {
          void startHydrationOnce().catch(() => {});
        }
      },
      true,
    );

    if (document.body.classList.contains('mh-search-modal-open')) {
      void startHydrationOnce().catch(() => {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installTriggers, {
      once: true,
    });
  } else {
    installTriggers();
  }
})();
